import TrashIcon from "assets/icons/TrashIcon";
import { Button } from "components/Button";
import { useEffect, useState } from "react";
import { useCampaignsStore } from "screens/Campaigns/store";
import { ICampaignsRule } from "screens/Campaigns/types";
import clsx from "clsx";

interface IProps {
  rule: ICampaignsRule;
  removeRule: (rule: ICampaignsRule) => Promise<void>;
  saveRule: (rule: ICampaignsRule) => Promise<void>;
}

const MAX_CAMPAIGN_RULE_LENGTH = 300;

export const CampaignsRule: React.FC<IProps> = ({
  rule,
  removeRule,
  saveRule
}) => {
  const {
    selectedCampaigns: selectedCampaign,
    campaignRules,
    setCampaignRules
  } = useCampaignsStore();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [initialRuleState, setInitialRuleState] = useState({
    title: rule.title,
    description: rule.description
  });
  const [isRemoving, setIsRemoving] = useState(false);

  const hasChanges =
    initialRuleState.title !== title ||
    initialRuleState.description !== description;

  const saveHandler = async () => {
    setIsSaving(true);
    await saveRule({ ...rule, title, description });
    setInitialRuleState({ title, description });
    setIsSaving(false);
  };

  const removeHandler = async () => {
    setIsRemoving(true);
    await removeRule(rule);
    setIsRemoving(false);
  };

  const onChangeTitleHandler = (title: string) => {
    setCampaignRules(
      campaignRules.map((r) => (r.id === rule.id ? { ...rule, title } : r))
    );
    setTitle(title);
  };

  const onChangeDescriptionHandler = (description: string) => {
    setCampaignRules(
      campaignRules.map((r) =>
        r.id === rule.id ? { ...rule, description } : r
      )
    );
    setTitle(title);
  };

  useEffect(() => {
    setTitle(rule.title);
    setDescription(rule.description);
  }, [rule]);

  return (
    <>
      <div
        className={clsx(
          "border border-[#FFFFFF40] p-4 rounded-lg relative group transition-all duration-300",
          {
            "!pointer-events-none !cursor-not-allowed !animate-pulse":
              isRemoving
          }
        )}
      >
        <div className="flex flex-col gap-[4px]">
          <p className="ml-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
            Rule title
          </p>
          <div className="bg-[#FFFFFF1A] py-[15.5px] px-[20px] rounded-[8px] flex items-center max-h-[48px]">
            <input
              type="text"
              placeholder="Type title here"
              className="w-full bg-transparent text-[14px] leading-[17px] font-[500] placeholder:opacity-50 placeholder:text-[#fff]"
              style={{ outline: "none" }}
              onChange={(e) => onChangeTitleHandler(e.target.value)}
              value={rule.title}
            />
          </div>
        </div>

        <div className="flex flex-col gap-[4px]">
          <p className="ml-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
            Rule description
          </p>

          <div className="relative mainBannerPreview">
            <textarea
              placeholder="Add description here"
              className="bg-[#FFFFFF1A] py-[15.5px] px-[20px] rounded-[8px] flex items-center min-h-[103px] max-h-[103px] w-full text-[14px] leading-[17px] font-[500] placeholder:opacity-50 placeholder:text-[#fff] custom-scrollbar"
              style={{
                outline: "none",
                resize: "none"
              }}
              maxLength={MAX_CAMPAIGN_RULE_LENGTH}
              value={rule.description}
              onChange={(e) => onChangeDescriptionHandler(e.target.value)}
            />

            <p className="absolute bottom-[12px] right-[20px] py-[4px] px-[6px] bg-[#3f3f3f] rounded-[8px] text-[#FFFFFF80]">
              {description.length || 0}
            </p>
          </div>
        </div>
        {selectedCampaign && hasChanges && (
          <div className="bg-gradient-to-t from-black to-transparent mt-6 flex items-center w-full">
            <Button
              extraClass="w-full rounded-[8px]"
              onClick={saveHandler}
              disabled={isSaving || !title || !description}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}

        <button
          className="dapp-page--grid-item--action-delete absolute -top-[12px] -right-[12px] rounded-lg opacity-0 invisible transition-all duration-300 group-hover:opacity-100 group-hover:visible hover:bg-[#1A1A1A]"
          onClick={(e) => {
            e.stopPropagation();
            removeHandler();
          }}
        >
          <TrashIcon width={16} height={16} />
        </button>
      </div>
    </>
  );
};
